import React, { useState, useEffect } from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    HomeOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    BankOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    AppstoreOutlined,
    FormOutlined
} from "@ant-design/icons";
import { Col, Row, Button, Input, Form, message } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { formatDateTime, formatLeadSource } from "../../../../utils/functions";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import SubmitButton from "../../../common/submitButton";
import Spinner from "../../../global/spinner";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../../lib/query-constants";

const EnhancedHotLeadsDetails = ({
    leadDetail,
    handleClose,
    keyParams
}) => {
    const [notes, setNotes] = useState("");
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { mutateAsync: saveExtraNotes, isPending: isSavingNotes } = useGenericMutation({
        endpoint: `leadinfo/notes`,
        onSuccess: () => {
            message.success("Notes saved successfully");
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.hotLeads.lists({ ...keyParams }),
                type: "active"
            })
        }
    });

    useEffect(() => {
        if (leadDetail?.notes) {
            setNotes(leadDetail.notes);
            form.setFieldsValue({ notes: leadDetail.notes });
        } else {
            setNotes("");
            form.setFieldsValue({ notes: "" });
        }
    }, [leadDetail, form]);

    if (!leadDetail) return null;

    const handleSubmitNotes = async (values) => {
        if (!values.notes) return;
        await saveExtraNotes({
            leadId: leadDetail._id,
            notes: values.notes,
        });
    };

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Address: <HomeOutlined />,
            "Event Name": <InfoCircleOutlined />,
            "Event Type": <InfoCircleOutlined />,
            "Event Date": <CalendarOutlined />,
            "Event Time": <ClockCircleOutlined />,
            "Guest Count": <TeamOutlined />,
            Company: <BankOutlined />,
            "Imported At": <CalendarOutlined />,
            Budget: <DollarOutlined />,
            "Notes/Inquiry": <InfoCircleOutlined />,
            Source: <AppstoreOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    return (
        <div className="p-6" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                title={
                    <div className="flex items-center gap-2">
                        <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                        <span style={{ fontSize: "16px", fontWeight: 600 }}>Hot Lead Details</span>
                    </div>
                }
            >
                <Row gutter={[24, 0]}>
                    {/* Basic Information Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Basic Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                {
                                    label: "Name",
                                    value: leadDetail.fullName ? leadDetail.fullName : `${leadDetail.firstName} ${leadDetail.lastName}`
                                },
                                { label: "Email", value: leadDetail?.email },
                                { label: "Phone", value: leadDetail?.phone },
                                { label: "Imported At", value: formatDateTime(leadDetail?.dateAsPerUserTimezone) },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Event Information Section */}
                    <Col span={24} className="py-4 border-b border-gray-200">
                        <div className="flex items-center gap-2 mb-4">
                            <CalendarOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Event Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                { label: "Event Name", value: leadDetail?.eventName },
                                { label: "Event Type", value: leadDetail?.eventType },
                                { label: "Event Date", value: leadDetail?.eventDate },
                                { label: "Guest Count", value: leadDetail?.guestCount },
                                { label: "Company", value: leadDetail?.company },
                                { label: "Imported At", value: formatDateTime(leadDetail?.dateAsPerUserTimezone) },
                                { label: "Source", value: formatLeadSource(leadDetail?.leadSource), className: "capitalize" },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        <span className={cn("text-sm font-semibold text-gray-900", item.className)}>{item.value || "—"}</span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Notes/Inquiry Section */}
                    {leadDetail?.inquiry && (
                        <Col span={24} className="border-b border-gray-200 py-4">
                            <div className="flex items-center gap-2 mb-3">
                                <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                                <span style={{ fontSize: "15px", fontWeight: 600 }}>Notes/Inquiry</span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap m-0">{leadDetail?.inquiry}</p>
                        </Col>
                    )}

                    {/* Extra Information Section (Notes Form) */}
                    <Col span={24} className="pt-4">
                        <div className="flex items-center gap-2 mb-4">
                            <FormOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Extra Information</span>
                        </div>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmitNotes}
                        >
                            <Form.Item
                                name="notes"
                                label="You can add extra information regarding lead"
                                rules={[{ required: true, message: 'Please add some notes' }]}
                            >
                                <Input.TextArea rows={4} placeholder="Add notes here..." />
                            </Form.Item>
                            <Form.Item>
                                <Button htmlType="submit" className="!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white">
                                    {isSavingNotes && <Spinner className={"!border-white"} />}
                                    Save Notes
                                </Button>
                            </Form.Item>
                        </Form>

                    </Col>
                </Row>
            </CommonCard>
        </div>
    );
};

export default EnhancedHotLeadsDetails;
